import { useId, useRef } from "react";
import { useIsMutating, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { api } from "../../lib/api";

export default function DeleteUserAction({ userId, name, mobile, returnTo = "/users" }: { userId: string; name: string | null; mobile: string; returnTo?: string }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const client = useQueryClient();
  const navigate = useNavigate();
  const saving = useIsMutating({ mutationKey: ["save-user", userId] }) > 0;
  const deletion = useMutation({
    mutationFn: () => api.deleteAdminUser(userId),
    onSuccess: async () => {
      dialog.current?.close();
      navigate(returnTo, { replace: true, state: { deletedUser: name || mobile } });
      client.removeQueries({ queryKey: ["tech-ops-user", userId], exact: true });
      await Promise.all(["tech-ops-users", "tech-ops-user", "tech-admin-workspace", "tech-admin-audit", "tech-ops-proxy-candidates", "tech-ops-dashboard"].map(key => client.invalidateQueries({ queryKey: [key] })));
    }
  });

  function open() {
    deletion.reset();
    dialog.current?.showModal();
    cancelButton.current?.focus();
  }

  return <>
    <button type="button" className="pa-secondary-button pa-delete-user-trigger" disabled={saving || deletion.isPending} onClick={open}>
      <Trash2 size={16} aria-hidden="true" /> Delete user
    </button>
    <dialog ref={dialog} className="pa-delete-user-dialog" role="alertdialog" aria-labelledby={titleId} aria-describedby={descriptionId}
      onCancel={event => { if (deletion.isPending) event.preventDefault(); }}>
      <h2 id={titleId}>Delete {name || mobile}?</h2>
      <p className="pa-delete-user-identity">{mobile}</p>
      <p id={descriptionId}>This removes the user's access and WhatsApp proxy mappings. Historical tasks and audit records will be retained. You cannot undo this from the dashboard.</p>
      {deletion.isError && <p className="pa-form-error" role="alert">{deletion.error instanceof Error ? deletion.error.message : "Could not delete the user. Try again."}</p>}
      <div className="pa-detail-actions">
        <button ref={cancelButton} type="button" className="pa-secondary-button" disabled={deletion.isPending} onClick={() => dialog.current?.close()}>Cancel</button>
        <button type="button" className="pa-delete-user-confirm" disabled={deletion.isPending} onClick={() => deletion.mutate()}>{deletion.isPending ? "Deleting…" : "Delete user"}</button>
      </div>
    </dialog>
  </>;
}
